/**
 * Which project the app is working on, per organisation.
 *
 * Chat is bound to a project — the agent's `createThread(title, projectId)` and
 * `verifyProjectOrganization` both take one — so "the selected project" is app
 * state, not a detail of the projects pane. It lives here so the pane that
 * chooses it and the pane that uses it do not have to know about each other.
 *
 * The rules about what a stored value means live in `lib/projectSelectionStore`,
 * where they can be tested without React; this is the React half — state, the
 * cross-window listener, and the setter.
 */
import { useCallback, useEffect, useState } from "react";
import {
  readSelections,
  SELECTION_STORAGE_KEY,
  selectionFor,
  withoutSelection,
  withSelection,
  writeSelections,
  type Selections,
} from "../lib/projectSelectionStore";

export function useSelectedProject(orgId: string | null): {
  selectedProjectId: string | null;
  selectProject: (projectId: string) => void;
  /** Drop this org's selection — used when the selected project is deleted. */
  forgetSelection: () => void;
} {
  const [selections, setSelections] = useState<Selections>(() => readSelections());

  // Pick up a selection made in another window of the same origin.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === SELECTION_STORAGE_KEY) setSelections(readSelections());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const selectProject = useCallback(
    (projectId: string) => {
      if (!orgId) return;
      setSelections((current) => {
        const next = withSelection(current, orgId, projectId);
        writeSelections(next);
        return next;
      });
    },
    [orgId],
  );

  const forgetSelection = useCallback(() => {
    if (!orgId) return;
    setSelections((current) => {
      const next = withoutSelection(current, orgId);
      writeSelections(next);
      return next;
    });
  }, [orgId]);

  return { selectedProjectId: selectionFor(selections, orgId), selectProject, forgetSelection };
}
